import React, { useCallback } from 'react'
import { useDropzone, type FileRejection } from 'react-dropzone'
import { Upload } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { apiClient } from '@/lib/api'
import { isDemoMode } from '@/demo/config'
import { useToast } from '@/hooks/use-toast'

interface UploadFile {
  id: string
  file: File
  status: 'pending' | 'uploading' | 'success' | 'error'
  progress: number
  error?: string
  invoiceId?: string
}

interface InvoiceUploadSectionProps {
  uploadedFiles: UploadFile[]
  setUploadedFiles: React.Dispatch<React.SetStateAction<UploadFile[]>>
  isUploading: boolean
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>
  onFilesUploaded?: (files: { invoiceId: string; filename: string }[]) => void
  maxFiles?: number
}

export function InvoiceUploadSection({
  uploadedFiles,
  setUploadedFiles,
  isUploading,
  setIsUploading,
  onFilesUploaded,
  maxFiles = 50
}: InvoiceUploadSectionProps) {
  const { toast } = useToast()
  const scrollListStyle: React.CSSProperties = {
    scrollbarWidth: 'thin',
    scrollbarColor: '#d97706 #fef3c7'
  }

  const uploadFiles = useCallback(async (filesToUpload: UploadFile[]) => {
    if (!filesToUpload.length) return

    setIsUploading(true)

    const successfulUploads: { invoiceId: string; filename: string }[] = []
    const duplicateUploads: { filename: string }[] = []
    const failedUploads: { filename: string; reason: string }[] = []

    try {
      for (const uploadFile of filesToUpload) {
        let progressInterval: ReturnType<typeof setInterval> | undefined

        try {
          setUploadedFiles(prev =>
            prev.map(f =>
              f.id === uploadFile.id
                ? { ...f, status: 'uploading', progress: 0 }
                : f
            )
          )

          const formData = new FormData()
          formData.append('file', uploadFile.file)

          progressInterval = setInterval(() => {
            setUploadedFiles(prev =>
              prev.map(f =>
                f.id === uploadFile.id && f.progress < 90
                  ? { ...f, progress: f.progress + 10 }
                  : f
              )
            )
          }, 200)

          const response = await apiClient.fetch('/invoices/upload', {
            method: 'POST',
            body: formData
          })

          if (response.ok) {
            const result = await response.json()

            if (result.duplicate) {
              duplicateUploads.push({ filename: uploadFile.file.name })

              setUploadedFiles(prev =>
                prev.map(f =>
                  f.id === uploadFile.id
                    ? {
                        ...f,
                        status: 'error',
                        progress: 0,
                        error: 'Duplicate file - already exists'
                      }
                    : f
                )
              )
            } else {
              setUploadedFiles(prev =>
                prev.map(f =>
                  f.id === uploadFile.id
                    ? {
                        ...f,
                        status: 'success',
                        progress: 100,
                        invoiceId: result.invoice.id
                      }
                    : f
                )
              )

              successfulUploads.push({
                invoiceId: result.invoice.id,
                filename: result.invoice.original_filename
              })
            }
          } else {
            const errorData = await response
              .json()
              .catch(() => ({ detail: 'Upload failed' }))
            const reason = errorData.detail || 'Upload failed'

            failedUploads.push({
              filename: uploadFile.file.name,
              reason
            })

            setUploadedFiles(prev =>
              prev.map(f =>
                f.id === uploadFile.id
                  ? {
                      ...f,
                      status: 'error',
                      progress: 0,
                      error: reason
                    }
                  : f
              )
            )
          }
        } catch (error) {
          console.error(error)

          failedUploads.push({
            filename: uploadFile.file.name,
            reason: 'Network error'
          })

          setUploadedFiles(prev =>
            prev.map(f =>
              f.id === uploadFile.id
                ? {
                    ...f,
                    status: 'error',
                    progress: 0,
                    error: 'Network error'
                  }
                : f
            )
          )
        } finally {
          if (progressInterval) {
            clearInterval(progressInterval)
          }
        }
      }
    } finally {
      setIsUploading(false)
    }

    if (successfulUploads.length > 0) {
      if (onFilesUploaded) {
        onFilesUploaded(successfulUploads)
      }

      setUploadedFiles([])
    }

    const successCount = successfulUploads.length
    const duplicateCount = duplicateUploads.length
    const failedCount = failedUploads.length

    toast({
      title: "Upload Summary",
      description: (
        <div className="space-y-2 text-sm">
          <p className="font-medium text-neutral-900">
            Successful: {successCount} | Failed: {failedCount} | Duplicates: {duplicateCount}
          </p>

          {duplicateCount > 0 && (
            <div className="space-y-1">
              <p className="font-medium text-neutral-900">Duplicates</p>
              <ul
                className="max-h-28 space-y-1 overflow-y-auto rounded border border-amber-200 bg-amber-50/60 pr-1 text-neutral-700 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-amber-100 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-amber-500 [&::-webkit-scrollbar-thumb]:hover:bg-amber-600"
                style={scrollListStyle}
              >
                {duplicateUploads.map(item => (
                  <li key={`duplicate-${item.filename}`} className="break-words text-xs leading-snug text-neutral-700">
                    • {item.filename}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {failedCount > 0 && (
            <div className="space-y-1">
              <p className="font-medium text-neutral-900">Failed</p>
              <ul
                className="max-h-28 space-y-1 overflow-y-auto rounded border border-amber-200 bg-amber-50/60 pr-1 text-neutral-700 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-amber-100 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-amber-500 [&::-webkit-scrollbar-thumb]:hover:bg-amber-600"
                style={scrollListStyle}
              >
                {failedUploads.map(item => (
                  <li key={`failed-${item.filename}`} className="break-words text-xs leading-snug text-neutral-700">
                    • {item.filename}{item.reason ? ` (${item.reason})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ),
      duration: 600000,
      variant: 'warning'
    })
  }, [onFilesUploaded, setIsUploading, setUploadedFiles, toast])

  const onDrop = useCallback((
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => {
    const remainingSlots = Math.max(maxFiles - uploadedFiles.length, 0)
    const allowedFiles = remainingSlots > 0 ? acceptedFiles.slice(0, remainingSlots) : []
    const hasTooManyFiles =
      acceptedFiles.length > allowedFiles.length ||
      fileRejections.some(rejection =>
        rejection.errors.some(error => error.code === 'too-many-files')
      )

    if (remainingSlots <= 0 || hasTooManyFiles) {
      toast({
        title: "File limit reached",
        description: `You can upload a maximum of ${maxFiles} file${maxFiles === 1 ? '' : 's'} at a time. Remove files before adding more.`,
        variant: 'destructive'
      })
    }

    if (!allowedFiles.length) {
      return
    }

    const newFiles: UploadFile[] = allowedFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      status: 'pending',
      progress: 0
    }))

    setUploadedFiles(prev => [...prev, ...newFiles])
    void uploadFiles(newFiles)
  }, [maxFiles, setUploadedFiles, toast, uploadFiles, uploadedFiles])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/tiff': ['.tiff', '.tif']
    },
    maxFiles,
    disabled: isUploading,
    // In demo mode never open the OS file picker — clicking the dropzone
    // uploads a synthetic invoice directly (see handleDemoClick).
    noClick: isDemoMode
  })

  const handleDemoClick = useCallback(() => {
    if (isUploading) return
    const file = new File([new Blob(['demo'])], 'vivacom_07_2026.pdf', { type: 'application/pdf' })
    void onDrop([file], [])
  }, [isUploading, onDrop])

  const clearAllFiles = () => {
    setUploadedFiles([])
  }

  return (
    <>
      {/* Upload Section */}
      <Card
        {...getRootProps()}
        {...(isDemoMode ? { onClick: handleDemoClick } : {})}
        className={cn(
          "flex-col items-center justify-center gap-2 self-stretch border border-dashed border-neutral-500 flex px-0 py-6 relative flex-1 grow bg-transparent rounded-xl min-h-[280px] cursor-pointer shadow-none",
          isDragActive && "bg-blue-50 border-blue-400",
          isUploading && "opacity-50 cursor-not-allowed"
        )}
      >
        <CardContent className="flex flex-col items-center gap-4 p-0 shadow-none">
          {/* Upload Area */}
          <div className="flex flex-col items-center gap-4 p-4 w-full max-w-md">
            <input {...getInputProps()} />
            <div className="flex flex-col w-10 h-10 items-center justify-center gap-1.5 relative">
              <Upload className={cn("w-10 h-10 text-neutral-500", isDragActive && "text-blue-500")} />
            </div>
            <div className="inline-flex items-center justify-center gap-2.5 relative flex-[0_0_auto]">
              <div className="relative flex items-center justify-center w-fit  font-text-lg-semi-bold font-[number:var(--text-lg-semi-bold-font-weight)] text-neutral-950 text-[length:var(--text-lg-semi-bold-font-size)] tracking-[var(--text-lg-semi-bold-letter-spacing)] leading-[var(--text-lg-semi-bold-line-height)] whitespace-nowrap [font-style:var(--text-lg-semi-bold-font-style)]">
                {(() => {
                  const uploadingCount = uploadedFiles.filter(f => f.status === 'uploading').length
                  const errorCount = uploadedFiles.filter(f => f.status === 'error').length
                  if (isUploading) {
                    const activeCount = uploadingCount || uploadedFiles.length
                    return `Uploading ${activeCount} file${activeCount === 1 ? '' : 's'}...`
                  }

                  if (errorCount > 0) {
                    return `${errorCount} file${errorCount === 1 ? '' : 's'} failed to upload`
                  }

                  if (uploadedFiles.length > 0) {
                    return `${uploadedFiles.length} file${uploadedFiles.length === 1 ? '' : 's'} uploaded`
                  }

                  return isDragActive ? "Drop files here..." : "Drag & Drop Invoices Here"
                })()}
              </div>
            </div>
            <div className="inline-flex items-center justify-center gap-2.5 relative flex-[0_0_auto]">
              <div className="relative flex items-center justify-center w-fit  font-text-sm-regular font-[number:var(--text-sm-regular-font-weight)] text-neutral-500 text-[length:var(--text-sm-regular-font-size)] text-center tracking-[var(--text-sm-regular-letter-spacing)] leading-[var(--text-sm-regular-line-height)] [font-style:var(--text-sm-regular-font-style)]">
                Supports PDF, PNG, JPEG, TIFF
                <br />
                Max {maxFiles} Files / 50MB each
              </div>
            </div>
          </div>

          {/* Upload Actions */}
          {uploadedFiles.length > 0 && (
            <div className="flex gap-2 w-full max-w-md px-4">
              <Button
                onClick={clearAllFiles}
                disabled={isUploading}
                variant="outline"
                className="flex-1"
              >
                Clear All
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

    </>
  )
}
